import React, { useState, useEffect } from "react";
import { Box, Chip, Link, Typography } from "@mui/material";
import ReportInputTable from "./ReportInputTable";
import ReportTable from "./ReportTable";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard from "./components/DataCard";
import BarChartIcon from "@mui/icons-material/BarChart";
import { useAuthStore } from "./store/authStore";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { isTeam } from "./utils/roles";
import { fetchProject } from "./projects/projectApi";
import { CATCH_ALL_LABEL } from "./projects/ProjectTagField";

const Reports = () => {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  // Two route shapes point here: /report-list/:id (own or another user's
  // full list) and /report-list/:userId/:reportId (a specific report deep
  // link) — react-router names the param differently per route, so both
  // must be read and reconciled here.
  const { id, userId: routeUserId } = useParams();
  const resolvedUserId = routeUserId || id;
  const [userId, setUserId] = useState(resolvedUserId || user?.id);
  // /report-list?project=<id|none>: one project's input files and runs, every
  // user's (staff only; the project page's counts link here).
  const [searchParams] = useSearchParams();
  const project = isTeam(user) ? searchParams.get("project") : null;
  const [projectInfo, setProjectInfo] = useState(null);

  useEffect(() => {
    if (resolvedUserId) setUserId(resolvedUserId);
  }, [resolvedUserId]);

  useEffect(() => {
    setProjectInfo(null);
    if (!project || project === "none") return;
    fetchProject(project).then((d) => setProjectInfo(d?.project || null)).catch(() => setProjectInfo(null));
  }, [project]);

  const projectTitle = project === "none"
    ? CATCH_ALL_LABEL
    : projectInfo ? `${projectInfo.id} · ${projectInfo.name}${projectInfo.company_name ? ` · ${projectInfo.company_name}` : ""}` : project;

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<BarChartIcon />}
          title="Reports"
          subtitle={project ? "One project's input files and runs, everyone's" : "Submit new analyses and review job history"}
        />
        {project && (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 2 }}>
            <Chip color="primary" label={projectTitle} onDelete={() => navigate(`/report-list/${user?.id}`)} />
            {project !== "none" && (
              <Link component={RouterLink} to={`/projects/${encodeURIComponent(project)}`}>Back to the project</Link>
            )}
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Clear the filter (✕) for your own list.
            </Typography>
          </Box>
        )}
        <DataCard noPadding>
          <ReportInputTable key={`in-${project || userId}`} userId={project ? null : userId} project={project} />
          <ReportTable key={`runs-${project || userId}`} userId={project ? null : userId} project={project} />
        </DataCard>
      </PageContainer>
    </PageShell>
  );
};

export default Reports;
